/**
 * UploadDialog — confirm an upload: the instances file (required), the
 * optional Self Perf Assessment file, and what is happening while it runs.
 *
 * Legacy shows both pickers side by side with an Upload File button
 * (UploadDownloadTemplate.vue, EIA layout :63-109); the self-perf picker stays
 * DISABLED until an instances file is chosen, because the self-perf file can
 * only go up together with one (it is attached to the portfolio the instances
 * file creates). Same rule here.
 *
 * Opened by the page once an instances file has been picked (the Upload File
 * buttons open the picker straight away), so that file is usually already
 * filled in; it can still be changed or removed here.
 *
 * While uploading, the dialog cannot be dismissed (the steps run in order and
 * a half-finished upload is undone by the flow itself), and `stage` says which
 * step is running.
 *
 * `selfPerf` (default true) shows the Self Perf Assessment slot. CCA passes
 * false: its upload takes the instances file only (legacy
 * UploadDownloadTemplate.vue CCA layout :3-60 has one file input).
 */
import { useState } from 'react';

import { ClearIcon, UploadIcon } from '@components/common/icons';
import { Alert, Button, Modal, Spinner } from '@components/ui';
import { cx } from '@lib/classNames';

import { UploadFileButton } from '../UploadFileButton';
import { useFileDrop } from '../useFileDrop';

import styles from './UploadDialog.module.css';

/** One file slot: its label, the chosen file (or not), choose / remove. */
function FileSlot({ label, hint, file, disabled, onFile, onFiles, onClear, onReject }) {
  // The slot takes a dropped file too, through the same checks as the button.
  // With `onFiles` it takes two (the instances slot, when the Self Perf slot
  // exists): the first fills this slot, the second the next one.
  const { dropProps, dragging } = useFileDrop({
    onFile,
    onFiles,
    onReject,
    disabled,
    max: onFiles ? 2 : 1,
  });
  return (
    <div
      className={cx(styles.slot, dragging && styles.dragging)}
      aria-disabled={disabled || undefined}
      {...dropProps}
    >
      <div className={styles.slotText}>
        <span className={styles.slotLabel}>{label}</span>
        <span className={file ? styles.fileName : styles.noFile} title={file?.name}>
          {file ? file.name : hint}
        </span>
      </div>
      <div className={styles.slotActions}>
        {file && !disabled ? (
          <button
            type="button"
            className={styles.clear}
            onClick={onClear}
            aria-label={`Remove ${file.name}`}
          >
            <ClearIcon />
          </button>
        ) : null}
        <UploadFileButton
          variant="secondary-solid"
          icon={<UploadIcon />}
          className={styles.choose}
          disabled={disabled}
          onFile={onFile}
          onReject={onReject}
        >
          {file ? 'Change' : 'Choose file'}
        </UploadFileButton>
      </div>
    </div>
  );
}

export function UploadDialog({
  initialInstancesFile = null,
  initialSelfPerfFile = null,
  nameError,
  stage,
  uploading = false,
  selfPerf = true,
  onUpload,
  onClose,
}) {
  const [instancesFile, setInstancesFile] = useState(initialInstancesFile);
  // Only a product with the Self Perf slot (EIA) can arrive with one.
  const [selfPerfFile, setSelfPerfFile] = useState(selfPerf ? initialSelfPerfFile : null);
  const [fileError, setFileError] = useState('');

  const pick = (setter) => (file) => {
    setFileError('');
    setter(file);
  };

  const clearInstances = () => {
    // Without an instances file there is nothing to attach a self-perf file to.
    setInstancesFile(null);
    setSelfPerfFile(null);
  };

  const canUpload = Boolean(instancesFile) && !nameError && !uploading;

  return (
    <Modal
      open
      // Not dismissable mid-upload: the steps must finish (or undo themselves).
      onClose={uploading ? () => {} : onClose}
      title="Upload File"
      footer={
        <div className={styles.actions}>
          <Button variant="secondary-solid" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            startIcon={<UploadIcon />}
            loading={uploading}
            disabled={!canUpload}
            onClick={() =>
              onUpload?.({ instancesFile, selfPerfFile: selfPerf ? selfPerfFile : null })
            }
          >
            Upload
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        {nameError ? (
          <Alert variant="warning" className={styles.alert}>
            Enter a valid Portfolio Name first: {nameError}
          </Alert>
        ) : null}

        <FileSlot
          label="Instances file *"
          hint="No file chosen (.xlsx)"
          file={instancesFile}
          disabled={uploading}
          onFile={pick(setInstancesFile)}
          // Two files dropped here: instances first, then the self-perf file.
          onFiles={
            selfPerf
              ? ([first, second]) => {
                  setFileError('');
                  setInstancesFile(first);
                  if (second) setSelfPerfFile(second);
                }
              : undefined
          }
          onClear={clearInstances}
          onReject={setFileError}
        />
        {selfPerf ? (
          <FileSlot
            label="Self Perf Assessment file (optional)"
            hint={instancesFile ? 'No file chosen (.xlsx)' : 'Choose the instances file first'}
            file={selfPerfFile}
            disabled={!instancesFile || uploading}
            onFile={pick(setSelfPerfFile)}
            onClear={() => setSelfPerfFile(null)}
            onReject={setFileError}
          />
        ) : null}

        {fileError ? (
          <p className={styles.error} role="alert">
            {fileError}
          </p>
        ) : null}

        {uploading && stage ? (
          <p className={styles.stage} role="status">
            <Spinner small />
            <span>{stage}</span>
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

export default UploadDialog;
